import api from "./api.service";
import { Coloc } from "../types/coloc";

export type { Coloc };

const SELECTED_COLOC_KEY = "selected_coloc";

export interface ColocDetails {
  id: string;
  name: string;
  introTxt: string;
  locationTxt: string;
  featuresTxt: string;
  amenitiesTxt: string;
  address: string;
  zipCode: string;
  city: string;
  countryCode: string;
  surface: number | null;
  nbBathrooms: number | null;
  nbRestrooms: number | null;
  nbFloors: number | null;
  nbKitchens: number | null;
  nbParkings: number | null;
  constructionYear: number | null;
  renovationYear: number | null;
  totalTantiemes: number | null;
  location: [number, number] | null;
  locationPrecisionDisplay: number;
  isWifi: boolean;
  isCleaningServices: boolean;
  isConcierge: boolean;
  isGarden: boolean;
  isPetAllowed: boolean;
  isElevator: boolean;
  isWashingMachine: boolean;
  isDryer: boolean;
  isDishwasher: boolean;
  isBalcony: boolean;
  isStorage: boolean;
  isBikeStorage: boolean;
  isAirConditioning: boolean;
  heatingType: number;
  isChargesElectricity: boolean;
  isChargesHotWater: boolean;
  isChargesWater: boolean;
  isChargesHeating: boolean;
  isRentControlled: boolean;
  rentReferenceMajored: number;
  calculationMode: string;
  /** Espace locataire : seulement les consommations, aucun montant. */
  showOnlyConsumption: boolean;
  slug: string;
  pictures: string[];
  nearbyPOIs: string[];
  dateCreated: string;
  dateModified: string;
}

/** Un manque bloquant pour la mise en ligne. `where` désigne l'écran qui le
 *  corrige — la liste et les libellés viennent de l'API, jamais du front :
 *  c'est le backend qui fait respecter la règle, il doit aussi l'énoncer. */
export interface MissingItem {
  code: string;
  label: string;
  where: 'details' | 'rooms';
}

export interface PublicationReadiness {
  ready: boolean;
  missing: MissingItem[];
}

export interface CreateColocPayload {
  name: string;
  address: string;
  zipCode: string;
  city: string;
  countryCode: string;
  slug?: string;
  /** [latitude, longitude] issues de la recherche d'adresse. Envoyees des la
   *  creation : la position est un bloquant de publication, la recueillir plus
   *  tard revient a l'oublier. */
  location?: [number, number];
  /** Colonnes NOT NULL de `colocs`, demandées par le wizard plutôt que remplies
   *  avec "" à l'insu du propriétaire. */
  introTxt?: string;
  featuresTxt?: string;
  amenitiesTxt?: string;
  locationTxt?: string;
}

class ColocService {
  async getColocs(): Promise<Coloc[]> {
    const response = await api.get("/colocs");
    return response.data;
  }

  async getColocById(id: string): Promise<Coloc> {
    const response = await api.get(`/colocs/${id}`);
    return response.data;
  }

  async getColocDetails(colocId: string): Promise<ColocDetails> {
    const response = await api.get(`/colocs/${colocId}/details`);
    return response.data;
  }

  async updateColocDetails(colocId: string, details: Partial<ColocDetails>): Promise<ColocDetails> {
    const response = await api.put(`/colocs/${colocId}/details`, details);
    return response.data;
  }

  async createColoc(data: CreateColocPayload): Promise<Coloc> {
    const response = await api.post("/colocs", data);
    return response.data;
  }

  async deleteColoc(colocId: string): Promise<void> {
    await api.delete(`/colocs/${colocId}`);
  }

  /** Ce qu'il reste à renseigner avant la mise en ligne. Gratuit à consulter :
   *  le bailleur doit savoir ce qu'on attend de lui avant de payer. */
  async publicationReadiness(colocId: string): Promise<PublicationReadiness> {
    const response = await api.get(`/colocs/${colocId}/publication-readiness`);
    return response.data;
  }

  /** Publie le site public (capacité publish_site + quota côté API). */
  async publish(colocId: string): Promise<void> {
    await api.post(`/colocs/${colocId}/publish`);
  }

  /** Dépublie le site public (toujours possible). */
  async unpublish(colocId: string): Promise<void> {
    await api.post(`/colocs/${colocId}/unpublish`);
  }
}

const createColocChangedEvent = (coloc: Coloc) => {
  return new CustomEvent("colocChanged", {
    bubbles: true,
    detail: coloc,
  });
};

export const getSelectedColoc = (): Coloc | null => {
  const storedColoc = localStorage.getItem(SELECTED_COLOC_KEY);
  if (storedColoc) {
    try {
      return JSON.parse(storedColoc);
    } catch {
      localStorage.removeItem(SELECTED_COLOC_KEY);
      return null;
    }
  }
  return null;
};

export const setSelectedColoc = (coloc: Coloc): void => {
  localStorage.setItem(SELECTED_COLOC_KEY, JSON.stringify(coloc));
  window.dispatchEvent(createColocChangedEvent(coloc));
};

export const colocService = new ColocService();
